import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { createPermissionChecker } from "./permissionChecker";
import { canAccessObject, resolvePermissionCode } from "./rbac";
import Firebase from "../Firebase/Firebase";

const auth = getAuth(Firebase);
const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [claims, setClaims] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);

      if (!user) {
        setClaims(null);
        setLoading(false);
        return;
      }

      const tokenResult = await user.getIdTokenResult(true);

      setClaims({
        role: tokenResult.claims.role,
        permissions: tokenResult.claims.permissions || {},
      });

      setLoading(false);
    });

    return () => unsub();
  }, []);

  const permissionChecker = useMemo(() => {
    return createPermissionChecker({
      permissions: (claims && claims.permissions) || {},
    });
  }, [claims]);

  const canAccessModule = useMemo(() => {
    return (objectKey, operation = "read") =>
      canAccessObject({
        role: claims?.role,
        permissions: claims?.permissions,
        objectKey,
        operation,
      });
  }, [claims]);

  const getObjectPermissionCode = useMemo(() => {
    return (objectKey) =>
      resolvePermissionCode({
        role: claims?.role,
        permissions: claims?.permissions,
        objectKey,
      });
  }, [claims]);

  const value = {
    firebaseUser,
    claims,
    permissionChecker,
    canAccessModule,
    getObjectPermissionCode,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
